import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RotateCcw, Undo2, ArrowLeft, Plus, X, Camera, Loader2 } from 'lucide-react';
import { jobsAPI, customWorkTypesAPI } from '../lib/api';
import { toast } from 'sonner';

const DOT_SPACING = 36;
const DOT_RADIUS = 2.5;
const SNAP_RADIUS = 22;

const ROOM_TYPES = [
    'Lounge', 'Dining Room', 'Kitchen', 'Study',
    'Bedroom', 'Bathroom', 'Cloakroom', 'Hallway', 'Landing', 'Other',
];
const PLASTERING_WALL_OPTIONS = ['Blue Grit & Overskim', 'Board & Overskim', 'Dot & Dab & Skim'];
const RENDERING_WALL_OPTIONS = ['Sand & Cement Render', 'Silicone Render', 'Monocouche Render', 'K-Rend', 'Tyrolean / Dry Dash'];
const WALL_WORK_OPTIONS = ['Blue Grit & Overskim', 'Board & Overskim', 'Dot & Dab & Skim'];
const CEILING_WORK_OPTIONS = ['Blue Grit & Overskim', 'Board & Overskim'];

// ── Pill selector — replaces native <select> for reliable mobile behaviour ──
function PillGroup({ value, onChange, options, testId, cols = 0 }) {
    return (
        <div className={cols === 2 ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap gap-2'} data-testid={testId}>
            {options.map(o => (
                <button
                    key={o}
                    type="button"
                    onClick={() => onChange(o)}
                    data-testid={testId ? `${testId}-${o.replace(/[\s&/]+/g, '-').toLowerCase()}` : undefined}
                    className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors text-left ${
                        value === o
                            ? 'bg-red-600 text-white border-red-600'
                            : 'bg-white text-slate-700 border-slate-200 active:bg-slate-100'
                    }`}
                >
                    {o}
                </button>
            ))}
        </div>
    );
}

function SectionHeading({ children }) {
    return <h3 className="font-heading text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">{children}</h3>;
}
function FieldLabel({ children }) {
    return <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{children}</label>;
}
function YesNoToggle({ value, onChange, testId }) {
    return (
        <div className="flex gap-1.5" data-testid={testId}>
            <button type="button" onClick={() => onChange(true)}
                className={`h-9 px-4 rounded-lg text-xs font-semibold transition-colors ${value === true ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                data-testid={testId ? `${testId}-yes` : undefined}>Yes</button>
            <button type="button" onClick={() => onChange(false)}
                className={`h-9 px-4 rounded-lg text-xs font-semibold transition-colors ${value === false ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                data-testid={testId ? `${testId}-no` : undefined}>No</button>
        </div>
    );
}
function QuestionRow({ label, children }) {
    return <div className="py-3 first:pt-0 last:pb-0"><p className="text-sm font-medium text-slate-700 mb-2">{label}</p>{children}</div>;
}

const inputCls = "w-full h-11 px-3 text-sm font-medium text-slate-900 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors placeholder:text-slate-300";

export default function RoomSurvey() {
    const { id: jobId, roomId } = useParams();
    const navigate = useNavigate();
    const isEditing = !!roomId;

    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const photoInputRef = useRef(null);

    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
    const [selectedDots, setSelectedDots] = useState([]);
    const [isClosed, setIsClosed] = useState(false);
    const [hoverDot, setHoverDot] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loadingRoom, setLoadingRoom] = useState(isEditing);
    const [jobType, setJobType] = useState('Plastering');
    // Work type options — base + custom types from DB
    const [wallOptionsPlastering, setWallOptionsPlastering] = useState(PLASTERING_WALL_OPTIONS);
    const [wallOptionsRendering, setWallOptionsRendering] = useState(RENDERING_WALL_OPTIONS);
    const [ceilingOptions, setCeilingOptions] = useState(CEILING_WORK_OPTIONS);

    // Form state
    const [roomType, setRoomType] = useState('');
    const [wallMeasurements, setWallMeasurements] = useState({});
    const [roomHeight, setRoomHeight] = useState('');
    const [allWallsSame, setAllWallsSame] = useState(null);
    const [singleWallWork, setSingleWallWork] = useState('');
    const [perWallWork, setPerWallWork] = useState({});
    const [ceilingPlastered, setCeilingPlastered] = useState(null);
    const [ceilingWorkType, setCeilingWorkType] = useState('');
    const [ceilingInsulated, setCeilingInsulated] = useState(null);
    const [wallsInsulated, setWallsInsulated] = useState(null);
    const [insulatedWalls, setInsulatedWalls] = useState([]);
    const [wallPrepRequired, setWallPrepRequired] = useState(null);
    const [prepHours, setPrepHours] = useState('');
    const [plasterBeads, setPlasterBeads] = useState('');
    const [additionalItems, setAdditionalItems] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [notes, setNotes] = useState('');

    // Load existing room data if editing
    useEffect(() => {
        // Fetch custom work types and merge into dropdowns
        customWorkTypesAPI.list().then(res => {
            const custom = res.data;
            const extraWallPlastering = custom
                .filter(wt => (wt.survey_context === 'wall' || wt.survey_context === 'both') && (wt.job_type_compat === 'Plastering' || wt.job_type_compat === 'Both'))
                .map(wt => wt.name);
            const extraWallRendering = custom
                .filter(wt => (wt.survey_context === 'wall' || wt.survey_context === 'both') && (wt.job_type_compat === 'Rendering' || wt.job_type_compat === 'Both'))
                .map(wt => wt.name);
            const extraCeiling = custom
                .filter(wt => (wt.survey_context === 'ceiling' || wt.survey_context === 'both'))
                .map(wt => wt.name);
            if (extraWallPlastering.length) setWallOptionsPlastering([...PLASTERING_WALL_OPTIONS, ...extraWallPlastering]);
            if (extraWallRendering.length) setWallOptionsRendering([...RENDERING_WALL_OPTIONS, ...extraWallRendering]);
            if (extraCeiling.length) setCeilingOptions([...CEILING_WORK_OPTIONS, ...extraCeiling]);
        }).catch(() => {});

        if (!isEditing) {
            // For new rooms, still fetch job to get job_type
            jobsAPI.get(jobId).then(res => setJobType(res.data.job_type || 'Plastering')).catch(() => {});
            return;
        }
        const loadRoom = async () => {
            try {
                const res = await jobsAPI.get(jobId);
                const room = res.data.areas?.find(a => a.id === roomId);
                if (!room) { toast.error('Room not found'); navigate(`/jobs/${jobId}`); return; }
                setJobType(res.data.job_type || 'Plastering');
                setRoomType(room.room_type || '');
                setRoomHeight(String(room.room_height || ''));
                const wm = {};
                (room.wall_measurements || []).forEach((v, i) => { wm[i] = String(v); });
                setWallMeasurements(wm);
                if (room.shape_dots?.length >= 3) {
                    setSelectedDots(room.shape_dots);
                    setIsClosed(true);
                }
                setAllWallsSame(room.all_walls_same ?? null);
                setSingleWallWork(room.single_wall_work || '');
                const pw = {};
                (room.per_wall_work || []).forEach((v, i) => { pw[i] = v; });
                setPerWallWork(pw);
                setCeilingPlastered(room.ceiling_plastered ?? null);
                setCeilingWorkType(room.ceiling_work_type || '');
                setCeilingInsulated(room.ceiling_insulated ?? null);
                setWallsInsulated(room.walls_insulated ?? null);
                setInsulatedWalls(room.insulated_walls || []);
                setWallPrepRequired(room.wall_prep_required ?? null);
                setPrepHours(room.prep_hours ? String(room.prep_hours) : '');
                setPlasterBeads(room.plaster_beads ? String(room.plaster_beads) : '');
                setAdditionalItems((room.additional_items || []).map(item => ({
                    id: item.id || Date.now(), description: item.description || '',
                    hours: item.hours ? String(item.hours) : '', value: item.value ? String(item.value) : ''
                })));
                setPhotos((room.photos || []).map((p, i) => ({ id: i, data: p, isExisting: true })));
                setNotes(room.notes || '');
            } catch { toast.error('Failed to load room'); navigate(`/jobs/${jobId}`); }
            finally { setLoadingRoom(false); }
        };
        loadRoom();
    }, [isEditing, jobId, roomId, navigate]);

    // Canvas logic
    const getDots = useCallback(() => {
        if (canvasSize.width === 0) return [];
        const dots = [];
        const cols = Math.floor((canvasSize.width - DOT_SPACING) / DOT_SPACING);
        const rows = Math.floor((canvasSize.height - DOT_SPACING) / DOT_SPACING);
        const offsetX = (canvasSize.width - cols * DOT_SPACING) / 2;
        const offsetY = (canvasSize.height - rows * DOT_SPACING) / 2;
        for (let r = 0; r <= rows; r++)
            for (let c = 0; c <= cols; c++)
                dots.push({ x: offsetX + c * DOT_SPACING, y: offsetY + r * DOT_SPACING });
        return dots;
    }, [canvasSize]);

    const findNearestDot = useCallback((x, y) => {
        let nearest = null, minDist = Infinity;
        for (const dot of getDots()) {
            const dist = Math.hypot(dot.x - x, dot.y - y);
            if (dist < minDist && dist < SNAP_RADIUS) { minDist = dist; nearest = dot; }
        }
        return nearest;
    }, [getDots]);

    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const w = containerRef.current.clientWidth;
                setCanvasSize({ width: w, height: Math.min(Math.max(w * 0.85, 300), 480) });
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || canvasSize.width === 0) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        canvas.width = canvasSize.width * dpr;
        canvas.height = canvasSize.height * dpr;
        canvas.style.width = `${canvasSize.width}px`;
        canvas.style.height = `${canvasSize.height}px`;
        ctx.scale(dpr, dpr);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);
        for (const dot of getDots()) { ctx.beginPath(); ctx.arc(dot.x, dot.y, DOT_RADIUS, 0, Math.PI * 2); ctx.fillStyle = '#CBD5E1'; ctx.fill(); }
        if (hoverDot && !isClosed) {
            ctx.beginPath(); ctx.arc(hoverDot.x, hoverDot.y, 13, 0, Math.PI * 2); ctx.fillStyle = 'rgba(220,38,38,0.10)'; ctx.fill();
            ctx.beginPath(); ctx.arc(hoverDot.x, hoverDot.y, DOT_RADIUS + 2, 0, Math.PI * 2); ctx.fillStyle = '#DC2626'; ctx.fill();
        }
        if (isClosed && selectedDots.length >= 3) {
            ctx.beginPath(); ctx.moveTo(selectedDots[0].x, selectedDots[0].y);
            for (let i = 1; i < selectedDots.length; i++) ctx.lineTo(selectedDots[i].x, selectedDots[i].y);
            ctx.closePath(); ctx.fillStyle = 'rgba(220,38,38,0.06)'; ctx.fill();
        }
        const drawLabel = (from, to, num) => {
            const mx = (from.x+to.x)/2, my = (from.y+to.y)/2, dx=to.x-from.x, dy=to.y-from.y, len=Math.hypot(dx,dy);
            if (!len) return;
            let nx=-dy/len, ny=dx/len;
            if (isClosed && selectedDots.length >= 3) {
                const cx=selectedDots.reduce((s,d)=>s+d.x,0)/selectedDots.length, cy=selectedDots.reduce((s,d)=>s+d.y,0)/selectedDots.length;
                if (Math.hypot(mx+nx*10-cx,my+ny*10-cy) < Math.hypot(mx-cx,my-cy)) { nx=-nx; ny=-ny; }
            }
            const lx=mx+nx*16, ly=my+ny*16, text=`W${num}`;
            ctx.font='600 10px "Public Sans",sans-serif'; ctx.textAlign='center'; ctx.textBaseline='middle';
            const pw=ctx.measureText(text).width+10, ph=17, r=4, x0=lx-pw/2, y0=ly-ph/2;
            ctx.beginPath(); ctx.moveTo(x0+r,y0); ctx.lineTo(x0+pw-r,y0); ctx.quadraticCurveTo(x0+pw,y0,x0+pw,y0+r);
            ctx.lineTo(x0+pw,y0+ph-r); ctx.quadraticCurveTo(x0+pw,y0+ph,x0+pw-r,y0+ph);
            ctx.lineTo(x0+r,y0+ph); ctx.quadraticCurveTo(x0,y0+ph,x0,y0+ph-r);
            ctx.lineTo(x0,y0+r); ctx.quadraticCurveTo(x0,y0,x0+r,y0); ctx.closePath();
            ctx.fillStyle='#1E293B'; ctx.fill(); ctx.fillStyle='#FFFFFF'; ctx.fillText(text,lx,ly+0.5);
        };
        const segs = [];
        for (let i=0;i<selectedDots.length-1;i++) segs.push([selectedDots[i],selectedDots[i+1]]);
        if (isClosed && selectedDots.length >= 3) segs.push([selectedDots[selectedDots.length-1],selectedDots[0]]);
        segs.forEach(([f,t],i) => { ctx.beginPath(); ctx.moveTo(f.x,f.y); ctx.lineTo(t.x,t.y); ctx.strokeStyle='#1E293B'; ctx.lineWidth=2.5; ctx.lineCap='round'; ctx.stroke(); drawLabel(f,t,i+1); });
        if (!isClosed && selectedDots.length > 0 && hoverDot) {
            const last=selectedDots[selectedDots.length-1];
            if (last.x!==hoverDot.x||last.y!==hoverDot.y) {
                ctx.beginPath(); ctx.moveTo(last.x,last.y); ctx.lineTo(hoverDot.x,hoverDot.y);
                ctx.strokeStyle='rgba(220,38,38,0.35)'; ctx.lineWidth=1.5; ctx.setLineDash([6,4]); ctx.stroke(); ctx.setLineDash([]);
            }
        }
        selectedDots.forEach((dot,i) => {
            if (i===0&&!isClosed&&selectedDots.length>=2) { ctx.beginPath(); ctx.arc(dot.x,dot.y,14,0,Math.PI*2); ctx.strokeStyle='rgba(220,38,38,0.30)'; ctx.lineWidth=2; ctx.stroke(); }
            ctx.beginPath(); ctx.arc(dot.x,dot.y,5.5,0,Math.PI*2); ctx.fillStyle=i===0?'#DC2626':'#1E293B'; ctx.fill();
            ctx.beginPath(); ctx.arc(dot.x,dot.y,2,0,Math.PI*2); ctx.fillStyle='#FFFFFF'; ctx.fill();
        });
    }, [canvasSize, selectedDots, isClosed, hoverDot, getDots]);

    const handleCanvasInteraction = useCallback((clientX, clientY) => {
        if (isClosed) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const nearest = findNearestDot(clientX - rect.left, clientY - rect.top);
        if (!nearest) return;
        if (selectedDots.length >= 3 && Math.hypot(nearest.x - selectedDots[0].x, nearest.y - selectedDots[0].y) < 1) {
            setIsClosed(true);
            const m = {}; for (let i = 0; i < selectedDots.length; i++) m[i] = '';
            setWallMeasurements(m); return;
        }
        if (selectedDots.length > 0 && nearest.x === selectedDots[selectedDots.length-1].x && nearest.y === selectedDots[selectedDots.length-1].y) return;
        for (let i = 1; i < selectedDots.length; i++) if (nearest.x === selectedDots[i].x && nearest.y === selectedDots[i].y) return;
        setSelectedDots(prev => [...prev, nearest]);
    }, [isClosed, selectedDots, findNearestDot]);

    const handleClick = (e) => handleCanvasInteraction(e.clientX, e.clientY);
    const handleTouchEnd = (e) => { e.preventDefault(); if (e.changedTouches.length > 0) { const t = e.changedTouches[0]; handleCanvasInteraction(t.clientX, t.clientY); }};
    const handleMouseMove = (e) => { if (isClosed) { setHoverDot(null); return; } const r=canvasRef.current?.getBoundingClientRect(); if(r) setHoverDot(findNearestDot(e.clientX-r.left,e.clientY-r.top)); };

    const handleResetCanvas = () => { setSelectedDots([]); setIsClosed(false); setWallMeasurements({}); setHoverDot(null); };
    const handleUndo = () => { if (!isClosed && selectedDots.length > 0) setSelectedDots(p => p.slice(0,-1)); };

    const addAdditionalItem = () => setAdditionalItems(p => [...p, { id: Date.now(), description: '', hours: '', value: '' }]);
    const updateAdditionalItem = (id, field, val) => setAdditionalItems(p => p.map(i => i.id === id ? { ...i, [field]: val } : i));
    const removeAdditionalItem = (id) => setAdditionalItems(p => p.filter(i => i.id !== id));

    const handlePhotoUpload = (e) => {
        const files = Array.from(e.target.files || []);
        files.forEach(file => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                setPhotos(prev => [...prev, { id: Date.now() + Math.random(), data: ev.target.result, isExisting: false }]);
            };
            reader.readAsDataURL(file);
        });
        if (photoInputRef.current) photoInputRef.current.value = '';
    };
    const removePhoto = (id) => setPhotos(p => p.filter(ph => ph.id !== id));
    const toggleInsulatedWall = (idx) => setInsulatedWalls(p => p.includes(idx) ? p.filter(w => w !== idx) : [...p, idx]);

    const wallCount = isClosed ? selectedDots.length : 0;
    const allMeasured = wallCount > 0 && Object.keys(wallMeasurements).length === wallCount &&
        Object.values(wallMeasurements).every(v => v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) > 0) &&
        roomHeight !== '' && !isNaN(parseFloat(roomHeight)) && parseFloat(roomHeight) > 0;

    const wallAreas = allMeasured ? Array.from({ length: wallCount }, (_, i) => parseFloat(wallMeasurements[i]) * parseFloat(roomHeight)) : [];
    const totalWallArea = wallAreas.reduce((s, a) => s + a, 0);

    const handleSaveRoom = async () => {
        if (!roomType) { toast.error('Please select a room type'); return; }
        if (!isClosed) { toast.error('Please complete the room drawing first'); return; }
        if (!allMeasured) { toast.error('Please fill in all wall measurements and room height'); return; }

        setSaving(true);
        try {
            const wallMeasArr = Array.from({ length: wallCount }, (_, i) => parseFloat(wallMeasurements[i]));
            const perWallArr = Array.from({ length: wallCount }, (_, i) => perWallWork[i] || '');
            const roomData = {
                room_type: roomType,
                room_height: parseFloat(roomHeight),
                wall_count: wallCount,
                wall_measurements: wallMeasArr,
                shape_dots: selectedDots,
                all_walls_same: allWallsSame === true,
                single_wall_work: singleWallWork,
                per_wall_work: perWallArr,
                ceiling_plastered: ceilingPlastered === true,
                ceiling_work_type: ceilingWorkType,
                ceiling_insulated: ceilingInsulated === true,
                walls_insulated: wallsInsulated === true,
                insulated_walls: insulatedWalls,
                wall_prep_required: wallPrepRequired === true,
                prep_hours: parseFloat(prepHours) || 0,
                plaster_beads: parseInt(plasterBeads) || 0,
                additional_items: additionalItems.map(item => ({
                    description: item.description, hours: parseFloat(item.hours) || 0, value: parseFloat(item.value) || 0
                })),
                photos: photos.map(p => p.data),
                notes: notes,
            };

            if (isEditing) {
                await jobsAPI.updateRoom(jobId, roomId, roomData);
                toast.success('Room updated');
            } else {
                await jobsAPI.addRoom(jobId, roomData);
                toast.success('Room saved');
            }
            navigate(`/jobs/${jobId}`);
        } catch (err) {
            console.error(err);
            toast.error('Failed to save room');
        } finally { setSaving(false); }
    };

    if (loadingRoom) {
        return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
    }

    const statusText = isClosed ? `${wallCount} walls drawn` : selectedDots.length === 0 ? 'Tap a dot to start drawing'
        : selectedDots.length === 1 ? 'Tap another dot to draw Wall 1'
        : `${selectedDots.length - 1} wall${selectedDots.length - 1 > 1 ? 's' : ''} drawn`;

    return (
        <div className="min-h-screen bg-white pb-6" data-testid="room-survey-page">
            <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-slate-200">
                <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <button onClick={() => navigate(`/jobs/${jobId}`)} data-testid="back-btn"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="font-heading text-lg font-bold text-slate-900 leading-tight tracking-tight">
                                {isEditing ? 'Edit Room' : 'Add Room'}
                            </h1>
                            <p className="text-[11px] text-slate-500 leading-tight mt-0.5" data-testid="status-text">{statusText}</p>
                        </div>
                    </div>
                    <div className="flex gap-1.5">
                        {!isClosed && selectedDots.length > 0 && (
                            <button onClick={handleUndo} data-testid="undo-btn"
                                className="h-8 px-2.5 flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                                <Undo2 className="w-3.5 h-3.5" /> Undo
                            </button>
                        )}
                        {!isEditing && (
                            <button onClick={handleResetCanvas} data-testid="reset-btn"
                                className="h-8 px-2.5 flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                                <RotateCcw className="w-3.5 h-3.5" /> Reset
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <div className="max-w-lg mx-auto">
                <div className="px-4 pt-4 pb-1">
                    <FieldLabel>Room Type</FieldLabel>
                    <PillGroup value={roomType} onChange={setRoomType} options={ROOM_TYPES} testId="room-type-select" cols={2} />
                </div>

                <div ref={containerRef} className="px-3 pt-3">
                    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white"
                        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <canvas ref={canvasRef} data-testid="room-canvas" onClick={handleClick} onTouchEnd={handleTouchEnd}
                            onMouseMove={handleMouseMove} onMouseLeave={() => setHoverDot(null)}
                            className="block w-full touch-none" style={{ cursor: isClosed ? 'default' : 'crosshair' }} />
                    </div>
                    {!isClosed && selectedDots.length >= 2 && (
                        <p className="text-center text-[11px] text-red-600/70 mt-2 font-medium">Tap the red starting dot to close the room shape</p>
                    )}
                </div>

                {isClosed && (
                    <div className="px-4 pt-5" data-testid="measurements-panel">
                        <div className="mb-5">
                            <FieldLabel>Room Height</FieldLabel>
                            <div className="relative">
                                <input type="number" inputMode="decimal" step="0.01" min="0" value={roomHeight}
                                    onChange={(e) => setRoomHeight(e.target.value)} placeholder="e.g. 2.4"
                                    data-testid="room-height-input" className={`${inputCls} pr-10 h-12 text-base`} />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium">m</span>
                            </div>
                        </div>
                        <div className="mb-5">
                            <FieldLabel>Wall Measurements</FieldLabel>
                            <div className="grid grid-cols-2 gap-2.5">
                                {Array.from({ length: wallCount }, (_, i) => (
                                    <div key={i}>
                                        <label className="block text-[11px] font-semibold text-slate-700 mb-1">Wall {i + 1}</label>
                                        <div className="relative">
                                            <input type="number" inputMode="decimal" step="0.01" min="0"
                                                value={wallMeasurements[i] || ''} onChange={(e) => setWallMeasurements(p => ({ ...p, [i]: e.target.value }))}
                                                placeholder="0.00" data-testid={`wall-${i+1}-input`} className={`${inputCls} pr-10`} />
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-medium">m</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {allMeasured && (
                            <div data-testid="summary-panel" className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
                                <SectionHeading>Summary</SectionHeading>
                                <div className="space-y-1.5">
                                    {Array.from({ length: wallCount }, (_, i) => {
                                        const len = parseFloat(wallMeasurements[i]), h = parseFloat(roomHeight);
                                        return (
                                            <div key={i} className="flex justify-between items-center text-sm">
                                                <span className="text-slate-600"><span className="font-semibold text-slate-800">Wall {i+1}</span> {len.toFixed(2)}m &times; {h.toFixed(2)}m</span>
                                                <span className="font-medium text-slate-700 tabular-nums">{(len*h).toFixed(2)} m&sup2;</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-3 pt-3 border-t border-slate-300 flex justify-between items-center">
                                    <span className="text-sm font-bold text-slate-900">Total Wall Area</span>
                                    <span className="text-lg font-bold text-red-600 tabular-nums" data-testid="total-area">{totalWallArea.toFixed(2)} m&sup2;</span>
                                </div>
                            </div>
                        )}

                        {/* Work Specification */}
                        <div className="border-t border-slate-200 pt-5 mb-6" data-testid="plastering-spec">
                            <SectionHeading>
                                {jobType === 'Rendering' ? 'Rendering Specification'
                                    : jobType === 'Both' ? 'Work Specification (Plastering & Rendering)'
                                    : 'Plastering Specification'}
                            </SectionHeading>
                            <QuestionRow label={jobType === 'Rendering' ? 'Are all walls being rendered the same way?' : 'Are all walls being treated the same way?'}>
                                <YesNoToggle value={allWallsSame} onChange={setAllWallsSame} testId="all-walls-same" />
                                {allWallsSame === true && <div className="mt-3">
                                    <PillGroup value={singleWallWork} onChange={setSingleWallWork}
                                        options={jobType === 'Plastering' ? wallOptionsPlastering : jobType === 'Rendering' ? wallOptionsRendering : [...wallOptionsPlastering, ...wallOptionsRendering]}
                                        testId="single-wall-work-select" />
                                </div>}
                                {allWallsSame === false && <div className="mt-3 space-y-3">{Array.from({ length: wallCount }, (_, i) => (
                                    <div key={i}>
                                        <label className="block text-[11px] font-semibold text-slate-600 mb-1.5">Wall {i+1}</label>
                                        <PillGroup value={perWallWork[i]||''} onChange={(v)=>setPerWallWork(p=>({...p,[i]:v}))}
                                            options={jobType === 'Plastering' ? wallOptionsPlastering : jobType === 'Rendering' ? wallOptionsRendering : [...wallOptionsPlastering, ...wallOptionsRendering]}
                                            testId={`wall-${i+1}-work-select`} />
                                    </div>
                                ))}</div>}
                            </QuestionRow>
                            {jobType !== 'Rendering' && (
                                <QuestionRow label="Is the ceiling being plastered?">
                                    <YesNoToggle value={ceilingPlastered} onChange={setCeilingPlastered} testId="ceiling-plastered" />
                                    {ceilingPlastered === true && <div className="mt-3 space-y-3">
                                        <PillGroup value={ceilingWorkType} onChange={setCeilingWorkType} options={ceilingOptions} testId="ceiling-work-select" />
                                        <div><p className="text-sm font-medium text-slate-700 mb-2">Is the ceiling being insulated?</p>
                                        <YesNoToggle value={ceilingInsulated} onChange={setCeilingInsulated} testId="ceiling-insulated" /></div>
                                    </div>}
                                </QuestionRow>
                            )}
                            <QuestionRow label="Are any walls being insulated?">
                                <YesNoToggle value={wallsInsulated} onChange={(v) => { setWallsInsulated(v); if (!v) setInsulatedWalls([]); }} testId="walls-insulated" />
                                {wallsInsulated === true && <div className="mt-3 flex flex-wrap gap-2" data-testid="insulated-walls-checklist">
                                    {Array.from({ length: wallCount }, (_, i) => {
                                        const checked = insulatedWalls.includes(i);
                                        return <button key={i} type="button" onClick={() => toggleInsulatedWall(i)} data-testid={`insulated-wall-${i+1}`}
                                            className={`h-9 px-3.5 rounded-lg text-xs font-semibold border transition-colors ${checked ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>Wall {i+1}</button>;
                                    })}
                                </div>}
                            </QuestionRow>
                            <QuestionRow label="Is any wall preparation required?">
                                <YesNoToggle value={wallPrepRequired} onChange={setWallPrepRequired} testId="wall-prep" />
                                {wallPrepRequired === true && <div className="mt-3"><label className="block text-[11px] font-semibold text-slate-600 mb-1">Preparation hours</label>
                                    <input type="number" inputMode="decimal" step="0.5" min="0" value={prepHours} onChange={(e) => setPrepHours(e.target.value)} placeholder="0" data-testid="prep-hours-input" className={inputCls} /></div>}
                            </QuestionRow>
                            <QuestionRow label="Number of plaster beads required">
                                <input type="number" inputMode="numeric" step="1" min="0" value={plasterBeads} onChange={(e) => setPlasterBeads(e.target.value)} placeholder="0" data-testid="plaster-beads-input" className={inputCls} />
                            </QuestionRow>
                        </div>

                        {/* Additional Items */}
                        <div className="border-t border-slate-200 pt-5 mb-6" data-testid="additional-items-section">
                            <div className="flex items-center justify-between mb-3">
                                <SectionHeading>Additional Items</SectionHeading>
                                <button type="button" onClick={addAdditionalItem} data-testid="add-item-btn"
                                    className="h-8 px-3 flex items-center gap-1 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors">
                                    <Plus className="w-3.5 h-3.5" /> Add Item
                                </button>
                            </div>
                            {additionalItems.length === 0 && <p className="text-sm text-slate-400 italic">No additional items.</p>}
                            <div className="space-y-3">{additionalItems.map((item, idx) => (
                                <div key={item.id} className="bg-slate-50 border border-slate-200 rounded-xl p-3 relative" data-testid={`additional-item-${idx}`}>
                                    <button type="button" onClick={() => removeAdditionalItem(item.id)} data-testid={`remove-item-${idx}`}
                                        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"><X className="w-4 h-4" /></button>
                                    <div className="space-y-2 pr-6">
                                        <div><label className="block text-[11px] font-semibold text-slate-600 mb-1">Description</label>
                                            <input type="text" value={item.description} onChange={(e) => updateAdditionalItem(item.id, 'description', e.target.value)}
                                                placeholder="e.g. Wall 3 Insulated Plaster Board" data-testid={`item-description-${idx}`} className={inputCls} /></div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div><label className="block text-[11px] font-semibold text-slate-600 mb-1">Additional Hours</label>
                                                <input type="number" inputMode="decimal" step="0.5" min="0" value={item.hours} onChange={(e) => updateAdditionalItem(item.id, 'hours', e.target.value)}
                                                    placeholder="0" data-testid={`item-hours-${idx}`} className={inputCls} /></div>
                                            <div><label className="block text-[11px] font-semibold text-slate-600 mb-1">Additional Value</label>
                                                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 font-medium">&pound;</span>
                                                    <input type="number" inputMode="decimal" step="0.01" min="0" value={item.value} onChange={(e) => updateAdditionalItem(item.id, 'value', e.target.value)}
                                                        placeholder="0.00" data-testid={`item-value-${idx}`} className={`${inputCls} pl-7`} /></div></div>
                                        </div>
                                    </div>
                                </div>
                            ))}</div>
                        </div>

                        {/* Photos */}
                        <div className="border-t border-slate-200 pt-5 mb-6" data-testid="photos-section">
                            <SectionHeading>Photos</SectionHeading>
                            <input ref={photoInputRef} type="file" accept="image/*" multiple capture="environment" onChange={handlePhotoUpload} className="hidden" />
                            <div className="flex flex-wrap gap-2">
                                {photos.map((photo) => (
                                    <div key={photo.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                                        <img src={photo.data} alt="" className="w-full h-full object-cover" />
                                        <button type="button" onClick={() => removePhoto(photo.id)}
                                            className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-red-600 transition-colors"><X className="w-3 h-3" /></button>
                                    </div>
                                ))}
                                <button type="button" onClick={() => photoInputRef.current?.click()} data-testid="upload-photo-btn"
                                    className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-slate-400 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-slate-600 transition-colors">
                                    <Camera className="w-5 h-5" /><span className="text-[10px] font-medium">Add</span>
                                </button>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="border-t border-slate-200 pt-5 mb-6" data-testid="notes-section">
                            <SectionHeading>Notes</SectionHeading>
                            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional comments..." rows={3}
                                data-testid="notes-input" className="w-full px-3 py-2.5 text-sm font-medium text-slate-900 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors placeholder:text-slate-300 resize-none" />
                        </div>

                        {/* Save Button */}
                        <button type="button" onClick={handleSaveRoom} disabled={saving} data-testid="save-room-btn"
                            className="w-full py-3.5 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-60 text-white font-heading text-base font-bold uppercase tracking-wider rounded-xl transition-colors flex items-center justify-center gap-2">
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            {isEditing ? 'Update Room' : 'Save Room'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
